import { keepPreviousData } from '@tanstack/react-query';

import { useGetDataSeriesForTarget } from '@/generated/client/diagram/diagram';
import type { getDataSeriesForTarget } from '@/generated/client/diagram/diagram';
import type { DataSeries } from '@/generated/model';

import { TIME_SERIES_QUERY_CACHE_TIME_MS, TIME_SERIES_GARBAGE_COLLECTION_TIME_MS } from '../../config/queryConfig';
import { DISABLED_TIME_WINDOW } from '../time-window/timeWindow';
import type { TimeWindowMicros } from '../time-window/timeWindow';
import { dataSeriesParamsSerializer } from './dataSeriesParamsSerializer';
import { getMeasureChannelIds } from './measureChannelIds';

type UseDataSeriesQueryParams = {
  targetId: number | undefined;
  timeWindow: TimeWindowMicros | undefined;
  selectedSeriesIds: string[];
};

type DataSeriesResponse = Awaited<ReturnType<typeof getDataSeriesForTarget>>;

function selectDataSeries(response: DataSeriesResponse): DataSeries[] {
  return response.data.series;
}

/**
 * Samples of the selected measure channels (speed and states come with every response). Toggling a
 * series changes the query key; keeping the previous data lets the chart stay mounted and only the
 * changed series animate in, instead of a spinner replacing the chart.
 */
export function useDataSeriesQuery({ targetId, timeWindow, selectedSeriesIds }: UseDataSeriesQueryParams) {
  const isEnabled = targetId !== undefined && timeWindow !== undefined && selectedSeriesIds.length > 0;

  return useGetDataSeriesForTarget(
    targetId ?? 0,
    { measureChannelIds: getMeasureChannelIds(selectedSeriesIds), ...(timeWindow ?? DISABLED_TIME_WINDOW) },
    {
      axios: { paramsSerializer: dataSeriesParamsSerializer },
      query: {
        enabled: isEnabled,
        retry: false,
        placeholderData: keepPreviousData,
        staleTime: TIME_SERIES_QUERY_CACHE_TIME_MS,
        gcTime: TIME_SERIES_GARBAGE_COLLECTION_TIME_MS,
        select: selectDataSeries,
      },
    }
  );
}
